import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { login } = useAuth();
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [serverWaking, setServerWaking] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Warm-up: despierta el backend (Render free se duerme tras inactividad)
  useEffect(() => {
    let cancelled = false;
    const base = process.env.REACT_APP_BACKEND_URL;
    const wake = async () => {
      for (let i = 0; i < 15 && !cancelled; i++) {
        try {
          const res = await fetch(`${base}/api/health`, { method: 'GET', cache: 'no-store' });
          if (res.ok) {
            if (!cancelled) setServerWaking(false);
            return;
          }
        } catch (e) {
          // servidor aún despertando, reintentar
        }
        await new Promise((r) => setTimeout(r, 4000));
      }
      if (!cancelled) setServerWaking(false);
    };
    wake();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (slug) api.getPublicBrand(slug).then((r) => { if (r.data?.brand_name) setBrand(r.data); }).catch(() => {});
  }, [slug]);

  const brandName = brand?.brand_name;
  const brandLogo = brand?.logo_url ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${brand.logo_url}` : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(formData.email, formData.password);
      toast.success('SalesPRO: ¡Bienvenido!');
      const u = result?.user;
      // Si debe cambiar contraseña, ir a /change-password
      if (u?.must_change_password) {
        navigate('/change-password');
      } else if (u?.role_id === 'cliente') {
        navigate('/client-portal');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      const status = error.response?.status;
      if (!error.response) {
        // Sin respuesta del servidor: probablemente arranque en frío
        toast.error('SalesPRO: El servidor está despertando, espera unos segundos y vuelve a intentar.');
      } else if (status === 401) {
        toast.error(error.response?.data?.detail || 'SalesPRO: Credenciales inválidas');
      } else {
        toast.error(error.response?.data?.detail || 'SalesPRO: No se pudo iniciar sesión, intenta de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: "url('https://customer-assets.emergentagent.com/job_presupuestos-dev-1/artifacts/essz7vgp_IMG_2426.jpg')"
        }}
        data-testid="login-background-image"
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#022C22] via-[#022C22]/40 to-transparent"></div>
        <div className="relative z-10 flex flex-col justify-end items-start p-12 text-white">
          <h1 className="font-heading text-5xl font-black mb-4 drop-shadow-lg">SalesPRO</h1>
          <p className="text-lg font-body drop-shadow-md">Sistema profesional de gestión de ventas multi-empresa</p>
          <div className="mt-6 px-3 py-1 bg-white/15 backdrop-blur-sm rounded-full text-sm">
            Powered by SalesPRO Platform
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-gradient-to-b from-white to-[#F0FBF7]">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <img
              src={brandLogo || "/brand/salespro_logo.jpg"}
              alt={brandName || "SalesPRO"}
              onError={(e) => { e.target.src = "/brand/salespro_logo.jpg"; }}
              className="mx-auto mb-4 h-24 w-24 rounded-2xl object-cover shadow-xl shadow-primary/30 ring-1 ring-primary/20"
              data-testid="login-mobile-logo"
            />
            <h2 className="font-heading text-3xl font-bold text-gray-900 mb-2" data-testid="login-title">
              Iniciar Sesión
            </h2>
            <p className="text-gray-600">Bienvenido a <span className="font-bold text-[#022C22]" data-testid="login-brand-name">{brandName || 'SalesPRO'}</span></p>
          </div>

          {serverWaking && (
            <div
              data-testid="server-waking-banner"
              className="mb-6 flex items-center gap-3 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800"
            >
              <Loader2 size={18} className="animate-spin shrink-0" />
              <span>Conectando con el servidor… (puede tardar ~30s la primera vez)</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email">Correo Electrónico</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                placeholder="tu@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="h-12"
              />
            </div>

            <div>
              <Label htmlFor="password">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPwd ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="h-12 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#022C22]"
                  data-testid="toggle-password-visibility"
                >
                  {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              className="w-full h-12 text-base font-semibold"
              disabled={loading}
            >
              {loading ? 'Iniciando...' : serverWaking ? 'Conectando…' : 'Iniciar Sesión'}
            </Button>
          </form>

          <div className="mt-8 text-center text-xs text-gray-500 space-y-1">
            <p>© {new Date().getFullYear()} SalesPRO — Todos los derechos reservados</p>
            <p>Hecho por <a href="https://www.7-fstudy.com" target="_blank" rel="noreferrer" className="font-medium text-[#00796B] hover:underline">www.7-fstudy.com</a></p>
          </div>
        </div>
      </div>
    </div>
  );
}